import React, { useState, useRef } from 'react';
import { Card, Upload, Input, Button, message, Spin, Space, Typography, Row, Col } from 'antd';
import { InboxOutlined, FileExcelOutlined, FileWordOutlined, RobotOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';

const { Dragger } = Upload;
const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;

const ExcelAnalistPage = () => {
    const { darkMode } = useTheme();
    const [files, setFiles] = useState([]);
    const [userPrompt, setUserPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState('');
    const resultRef = useRef(null);

  const handleWordExport = () => {
    if (!result) return;
    
    const header = "<html xmlns:o='urn:schemas-microsoft-com:office:office' " +
        "xmlns:w='urn:schemas-microsoft-com:office:word' " +
        "xmlns='http://www.w3.org/TR/REC-html40'>" +
        "<head><meta charset='utf-8'><title>Export HTML to Word Document with JavaScript</title>" +
        "<style>body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; line-height: 1.5; margin: 1.5cm; text-align: justify; }</style>" + 
        "</head><body>";
    const footer = "</body></html>";
    const sourceHTML = header + `<div style="font-family: 'Times New Roman', serif;">${resultRef.current.innerHTML}</div>` + footer;

    const source = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(sourceHTML);
    const fileDownload = document.createElement("a");
    document.body.appendChild(fileDownload);
    fileDownload.href = source;
    fileDownload.download = 'Analiz_Raporu.doc';
    fileDownload.click();
    document.body.removeChild(fileDownload);
  };

  const handleAnalyze = async () => {
    if (files.length === 0) {
      message.error('Lütfen en az bir Excel dosyası yükleyin.');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    if (userPrompt) {
      formData.append('user_prompt', userPrompt);
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/excel-analysis/analyze`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult(response.data.analysis);
      message.success('Analiz tamamlandı!');
    } catch (error) {
      console.error('Analysis error:', error);
      message.error('Analiz sırasında bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    multiple: true,
    onRemove: (file) => {
      setFiles(prev => prev.filter(f => f.uid !== file.uid));
    },
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                     file.type === 'application/vnd.ms-excel' || 
                     file.name.endsWith('.xlsx') || 
                     file.name.endsWith('.xls');
                     
      if (!isExcel) {
        message.error(`${file.name} geçerli bir Excel dosyası değil!`);
        return Upload.LIST_IGNORE;
      }
      setFiles(prev => [...prev, file]);
      return false; 
    },
    fileList: files,
  };

  const renderBold = (str) => {
    const parts = str.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => 
      part.startsWith('**') && part.endsWith('**') 
        ? <strong key={i}>{part.slice(2, -2)}</strong> 
        : part
    );
  };

  const renderContent = (text) => {
    const lines = text.split('\n');
    return lines.map((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={index} style={{ height: 12 }} />;

      if (line.startsWith('###### ')) {
        return <h6 key={index} style={styles.h6}>{line.replace('###### ', '')}</h6>;
      }
      if (line.startsWith('##### ')) {
        return <h6 key={index} style={styles.h6}>{line.replace('##### ', '')}</h6>;
      }
      if (line.startsWith('#### ')) {
        return <h5 key={index} style={styles.h5}>{line.replace('#### ', '')}</h5>;
      }
      if (line.startsWith('### ')) {
        return <h4 key={index} style={styles.h4}>{line.replace('### ', '')}</h4>;
      }
      if (line.startsWith('## ')) {
        return <h3 key={index} style={styles.h3}>{line.replace('## ', '')}</h3>;
      }
      if (line.startsWith('# ')) {
        return <h2 key={index} style={styles.h2}>{line.replace('# ', '')}</h2>;
      }

      const numListMatch = trimmed.match(/^(\d+(?:\.\d+)*\.)\s+(.+)/);
      if (numListMatch) {
        return (
          <div key={index} style={styles.numListItem}>
            <span style={styles.numBadge}>{numListMatch[1]}</span>
            <span style={styles.numContent}>{renderBold(numListMatch[2])}</span>
          </div>
        );
      }

      if (line.startsWith('- ') || line.startsWith('* ')) {
        const content = line.replace(/^[-*]\s/, '');
        return (
          <div key={index} style={styles.bulletItem}>
            <span style={styles.bullet}>•</span>
            <span>{renderBold(content)}</span>
          </div>
        );
      }

      return (
        <p key={index} style={styles.paragraph}>
          {renderBold(line)}
        </p>
      );
    });
  };

  const styles = {
    h2: {
      fontSize: '1.35rem',
      fontWeight: 600,
      marginTop: 28,
      marginBottom: 12,
      color: 'inherit',
      borderBottom: darkMode ? '1px solid #303030' : '1px solid #e8e8e8',
      paddingBottom: 8,
    },
    h3: {
      fontSize: '1.15rem',
      fontWeight: 600,
      marginTop: 20,
      marginBottom: 10,
      color: 'inherit',
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 600,
      marginTop: 14,
      marginBottom: 8,
      color: 'inherit',
      opacity: 0.9,
    },
    h5: {
      fontSize: '0.95rem',
      fontWeight: 600,
      marginTop: 12,
      marginBottom: 6,
      color: 'inherit',
      opacity: 0.88,
    },
    h6: {
      fontSize: '0.9rem',
      fontWeight: 600,
      marginTop: 10,
      marginBottom: 4,
      color: 'inherit',
      opacity: 0.85,
    },
    paragraph: {
      marginBottom: 10,
      lineHeight: 1.7,
    },
    numListItem: {
      display: 'flex',
      gap: 12,
      marginBottom: 10,
      alignItems: 'flex-start',
    },
    numBadge: {
      flexShrink: 0,
      minWidth: 32,
      fontWeight: 600,
      fontSize: '0.9rem',
      color: darkMode ? '#177ddc' : '#1677ff',
    },
    numContent: {
      lineHeight: 1.7,
      flex: 1,
    },
    bulletItem: {
      display: 'flex',
      gap: 10,
      marginBottom: 8,
      marginLeft: 8,
      alignItems: 'flex-start',
    },
    bullet: {
      color: darkMode ? '#177ddc' : '#1677ff',
      fontWeight: 'bold',
      flexShrink: 0,
    },
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>
        <RobotOutlined style={{ marginRight: 10 }} />
        Excel Analisti
      </Title>
      <Paragraph>
        Excel dosyanızı yükleyin ve yapay zeka destekli analizler alın.
      </Paragraph>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={10}>
          <Card title="Veri Yükleme ve İstek" bordered={false} style={{ height: '100%' }}>
            <Dragger {...uploadProps} style={{ padding: 20, background: 'var(--bg-color)' }}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Tıklayın veya dosyayı bu alana sürükleyin</p>
              <p className="ant-upload-hint">
                Tekli veya toplu Excel dosyası yüklemesi desteklenir.
              </p>
            </Dragger>

            <div style={{ marginTop: 24 }}>
              <Title level={5}>Özelleştirilmiş İstek</Title>
              <TextArea 
                rows={4} 
                placeholder="Örn: Bu verideki satış trendlerini analiz et ve gelecek ay için tahminlerde bulun."
                value={userPrompt}
                onChange={(e) => setUserPrompt(e.target.value)}
              />
            </div>

            <Button 
              type="primary" 
              icon={<RobotOutlined />} 
              size="large" 
              block 
              style={{ marginTop: 24 }}
              onClick={handleAnalyze}
              loading={loading}
              disabled={files.length === 0}
            >
              {loading ? 'Analiz Ediliyor...' : 'Analiz Et'}
            </Button>
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card 
            title="Analiz Sonucu" 
            bordered={false} 
            style={{ height: '100%', minHeight: 500 }}
            extra={
              result && (
                <Space>
                  <Button icon={<FileWordOutlined />} onClick={handleWordExport}>Word İndir</Button>
                </Space>
              )
            }
          >
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Spin size="large" tip="Excel Analisti verilerinizi inceliyor..." />
              </div>
            ) : result ? (
              <div 
                ref={resultRef} 
                className={`excel-analiz-cikti ${darkMode ? 'excel-analiz-dark' : ''}`}
                style={{ 
                  padding: '24px 28px', 
                  background: darkMode ? '#1a1a1a' : '#fafafa',
                  borderRadius: 12, 
                  border: darkMode ? '1px solid #303030' : '1px solid #e8e8e8',
                  color: darkMode ? 'rgba(255,255,255,0.92)' : '#262626',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', 
                  lineHeight: '1.6', 
                  textAlign: 'left', 
                  fontSize: '14px',
                  maxHeight: '60vh',
                  overflowY: 'auto',
                  boxShadow: darkMode ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
                }}
              >
                {renderContent(result)}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#999', marginTop: 100 }}>
                <FileExcelOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <p>Analiz sonucunu görmek için bir dosya yükleyip analiz işlemini başlatın.</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ExcelAnalistPage;